import { BookOpen, Calculator, Pill, Search } from 'lucide-react';
import MiniSearch from 'minisearch';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMedications } from '@/features/meds/api';
import { useEffectiveProtocols } from '@/features/protocols/api';
import { TOOLS } from '@/features/tools/registry';
import { cn } from '@/lib/utils';
import { Dialog } from './ui';

interface SearchDoc {
  id: string;
  type: 'protocol' | 'medication' | 'tool';
  title: string;
  subtitle: string;
  keywords: string;
  body: string;
  url: string;
}

const TYPE_META = {
  protocol: { label: 'Protocol', Icon: BookOpen },
  medication: { label: 'Medication', Icon: Pill },
  tool: { label: 'Tool', Icon: Calculator },
};

/** Offline-capable search across cached protocols, the formulary, and tools. */
export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const protocols = useEffectiveProtocols();
  const meds = useMedications();

  const index = useMemo(() => {
    const docs: SearchDoc[] = [
      ...TOOLS.map((t) => ({ id: `tool:${t.id}`, type: 'tool' as const, title: t.title, subtitle: t.description, keywords: t.keywords.join(' '), body: '', url: `/tools/${t.id}` })),
      ...(meds.data?.medications ?? []).map((m) => ({
        id: `medication:${m.slug}`,
        type: 'medication' as const,
        title: m.genericName,
        subtitle: [m.brandNames.join(', '), m.drugClass].filter(Boolean).join(' · '),
        keywords: [...m.brandNames, ...m.indications].join(' '),
        body: m.doses.map((d) => d.indication).join(' '),
        url: `/meds/${m.slug}`,
      })),
      ...(protocols.data ?? []).map((p) => ({
        id: `protocol:${p.id}`,
        type: 'protocol' as const,
        title: p.code ? `${p.code} ${p.title}` : p.title,
        subtitle: p.summary ?? '',
        keywords: p.tags.join(' '),
        body: p.body_md.replace(/[#*_>|`-]/g, ' '),
        url: `/protocols/${p.id}`,
      })),
    ];
    const ms = new MiniSearch<SearchDoc>({
      fields: ['title', 'keywords', 'subtitle', 'body'],
      storeFields: ['type', 'title', 'subtitle', 'url'],
      searchOptions: { boost: { title: 4, keywords: 2, subtitle: 1.5 }, prefix: true, fuzzy: 0.2 },
    });
    ms.addAll(docs);
    return ms;
  }, [protocols.data, meds.data]);

  const results = useMemo(
    () => (query.trim().length < 2 ? [] : (index.search(query).slice(0, 20) as unknown as (SearchDoc & { score: number })[])),
    [index, query],
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const go = (url: string) => {
    onClose();
    navigate(url);
  };

  return (
    <Dialog open={open} onClose={onClose} title="Search" size="lg">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-stone-400" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && results[active]) {
              go(results[active].url);
            }
          }}
          placeholder="Protocols, medications, tools…"
          aria-label="Search"
          className="h-12 w-full rounded-md border border-stone-300 bg-white pl-10 pr-3 text-base outline-none focus:border-stone-900 focus:ring-1 focus:ring-stone-900 dark:focus:border-stone-200 dark:focus:ring-stone-200 dark:border-stone-700 dark:bg-ink-950"
        />
      </div>
      <ul className="mt-3 space-y-1" role="listbox">
        {results.map((r, i) => {
          const { Icon, label } = TYPE_META[r.type];
          return (
            <li key={r.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onClick={() => go(r.url)}
                onMouseEnter={() => setActive(i)}
                className={cn('flex w-full items-start gap-3 rounded-md px-3 py-2 text-left', i === active ? 'bg-stone-100 dark:bg-ink-800' : '')}
              >
                <Icon className="mt-0.5 size-4 shrink-0 text-stone-400" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{r.title}</span>
                  {r.subtitle && <span className="block truncate text-sm text-stone-500">{r.subtitle}</span>}
                </span>
                <span className="label-caps mt-0.5 text-stone-400">{label}</span>
              </button>
            </li>
          );
        })}
        {query.trim().length >= 2 && results.length === 0 && <li className="px-3 py-6 text-center text-sm text-stone-500">No matches for “{query}”.</li>}
        {query.trim().length < 2 && <li className="px-3 py-6 text-center text-sm text-stone-500">Search works offline across saved protocols and medications.</li>}
      </ul>
    </Dialog>
  );
}
